import { setupIonicReact, IonApp, IonContent } from '@ionic/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import React, {
  type RefObject,
  Suspense,
  createContext,
  lazy,
  useContext,
  useEffect,
  useRef,
} from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, useLocation } from 'react-router-dom'

import { ErrorFallbackFullScreen } from './components/common/ErrorFallback'
import { LoadingFallbackFullScreen } from './components/common/LoadingFallback'
import { useIsOnlineData, useMyPage } from './module/myPage/hook'
import { useSconnStudyDataApi } from './module/sconnStudyData/context'
import { useNetworkStatus } from './util/hooks/useNetworkStatus'

import './components/common/DateTimePicker.css'
import './components/common/Modal.css'

setupIonicReact()

const queryClient = new QueryClient()

const UserInformation = lazy(async () => await import('./pages/UserInformation'))

const ContentRefContext = createContext<RefObject<HTMLIonContentElement> | null>(null)
export const useContentRef = () => useContext(ContentRefContext)

function App() {
  const location = useLocation()
  const isOnline = useNetworkStatus()
  const { setIsOnline } = useIsOnlineData()
  const { isTokenReady } = useSconnStudyDataApi()
  const contentRef = useRef<HTMLIonContentElement>(null)

  useEffect(() => {
    contentRef.current?.scrollToTop(300)
  }, [location])

  useEffect(() => {
    setIsOnline(isOnline)
  }, [isOnline])

  return (
    <IonApp>
      <IonContent ref={contentRef}>
        <QueryClientProvider client={queryClient}>
          {isTokenReady ? (
            <>
              <ErrorBoundary FallbackComponent={ErrorFallbackFullScreen}>
                <Suspense fallback={<LoadingFallbackFullScreen />}>
                  <UserInformation />
                </Suspense>
              </ErrorBoundary>
              <ContentRefContext.Provider value={contentRef}>
                <Outlet />
              </ContentRefContext.Provider>
            </>
          ) : (
            <LoadingFallbackFullScreen />
          )}
        </QueryClientProvider>
      </IonContent>
    </IonApp>
  )
}

export default App
